import { exists } from 'base/helpers';
import { fromNested, toNested } from './nested';
import {
  Component,
  ComponentStyle,
  IX2,
  NestedNode,
  NestedTagNode,
  WfAsset,
} from './schema';
import {
  applyClassIdUpdates,
  componentStyleTypeclass,
  mergeStyles,
  resolveComboClass,
} from './style_util';

/** Creates a page component which arranges the sections into a vertical stack */
export function createPageComponent(
  pageName: string,
  sections: readonly {
    name: string;
    component: Component;
    isFooter?: boolean;
    isNavbar?: boolean;
  }[],
): Component | undefined {
  if (sections.length === 0) {
    return;
  }

  // 1. Prefix node IDs from each input component, so they don't collide
  sections = sections.map((s, i) => ({
    ...s,
    component: prefixComponentNodeIds(`rlS${i}-`, s.component),
  }));
  // 1.2. Rename the node classes to match the section names
  //   (except for the navbar and footer)
  const sectionClassNames = chooseSectionClassNames(
    pageName,
    sections.map((s) => s.name),
  );
  sections = sections.map((s, index) => {
    if (s.isNavbar || s.isFooter) return s;
    const newName = sectionClassNames[index];
    return {
      ...s,
      component: newName
        ? renameComponentClasses(newName, s.component)
        : s.component,
    };
  });

  // 2. Merge all the styles
  // 2.1. Collect styles from all components
  const classIdUpdates = new Map<string, string>();
  let styles: ComponentStyle[] = [];
  {
    for (const s of sections) {
      const result = mergeStyles(
        styles,
        s.component.payload.styles,
        componentStyleTypeclass,
      );
      styles = result.styles;
      for (const [k, v] of result.idUpdates) classIdUpdates.set(k, v);
    }
  }
  // 2.2. Add the wrapper styles
  const result = mergeStyles(
    styles,
    [createEmptyClass('page-wrapper'), createEmptyClass('main-wrapper')],
    componentStyleTypeclass,
  );
  styles = result.styles;
  for (const [k, v] of result.idUpdates) classIdUpdates.set(k, v);
  // 2.3. Apply ID updates
  sections = sections.map((s) => ({
    ...s,
    component: applyClassIdUpdates(classIdUpdates, s.component),
  }));
  const stylesById = new Map(styles.map((s) => [s._id, s]));

  // 3. Create the stack of nodes
  const navbar = sections[0].isNavbar ? sections[0] : undefined;
  const last = sections[sections.length - 1];
  const footer = last && last.isFooter && last !== navbar ? last : undefined;
  const bodySections = sections.filter((s) => s !== navbar && s !== footer);
  const bodyNodes = bodySections.flatMap((s) =>
    toNested(s.component.payload.nodes),
  );
  const root = createBlocksNode(
    'rlPageWrapper',
    resolveComboClass(['page-wrapper'], stylesById),
    [
      navbar && toNested(navbar.component.payload.nodes),
      createBlocksNode(
        'rlMainWrapper',
        resolveComboClass(['main-wrapper'], stylesById),
        bodyNodes,
        'main',
      ),
      footer && toNested(footer.component.payload.nodes),
    ],
  );

  // 4. Create output
  const allComponents = sections.map((s) => s.component);
  return {
    // Webflow crashes if we don't include a "meta" field.
    // Let's just use the "meta" from the first component.
    ...allComponents[0],
    payload: {
      ix1: [],
      ix2: mergeIX2(allComponents.map((x) => x.payload.ix2)),
      assets: mergeAssets(allComponents.map((x) => x.payload.assets)),
      nodes: fromNested([root]),
      styles,
    },
  };
}

function renameComponentClasses(
  newComponentName: string,
  component: Component,
): Component {
  const stylesById = new Map(component.payload.styles.map((s) => [s._id, s]));
  const firstNode = component.payload.nodes[0];
  if (!firstNode || firstNode.text || firstNode.classes.length !== 1) {
    return component;
  }
  const firstNodeClassName = stylesById.get(firstNode.classes[0])?.name || '';
  const match = firstNodeClassName.match(/^section_(.+)/);
  if (!match) return component;
  const componentName = match[1];
  const newStyles = component.payload.styles.map((s) => {
    if (s.type !== 'class') return s;
    return {
      ...s,
      name: s.name.replace(componentName, newComponentName),
    };
  });
  return { ...component, payload: { ...component.payload, styles: newStyles } };
}

export function chooseSectionClassNames(
  pageName: string,
  sectionNames: readonly string[],
): (string | undefined)[] {
  const prefix = `${cleanClassNamePart(pageName)}`;
  if (prefix.length === 0) {
    return Array(sectionNames.length).fill(undefined);
  }
  const sectionParts = sectionNames.map((s) => {
    const name = cleanClassNamePart(s);
    if (name.startsWith(prefix)) {
      const stripped = cleanClassNamePart(name.substring(prefix.length));
      if (stripped) return stripped;
    }
    return name || 'section';
  });

  const result: string[] = [];
  // Map of each class name to how many times we've seen it.
  const count = sectionParts.reduce((map, part) => {
    map.set(part, (map.get(part) ?? 0) + 1);
    return map;
  }, new Map<string, number>());
  // If we see the same class name twice, add in a index to the end so we don't
  // generate duplicates.
  for (const part of count.keys()) {
    const numInstances = count.get(part)!;
    if (numInstances === 1) {
      result.push(`${prefix}_${part}`);
      continue;
    }
    for (let i = 0; i < numInstances; i++) {
      result.push(`${prefix}_${part}-${i + 1}`);
    }
  }

  return result;
}

const SECTION_WORDS = [
  'Section', // en, fr-FR
  'Abschnitt', // de-DE
  'Sección de', // es-ES
  'Sección', // es-ES
  'अनुभाग', // hi-IN
  'Seksi', // id-ID
  'Sezione', // it-IT
  '섹션', // ko-KR
  'खण्ड', // ne-NP
  'Sectie', // nl-NL
  'Seção', // pt-BR
  'Secţiune', // ro-RO
  'Sekcja', // pl-PL
  'Sekce', // cs-CZ
  'Секция', // bg-BG
  'Seksjon', // no-NO
  'Sekcia', // sk-SK
].map((x) => removeAccents(x.toLowerCase()));
// In these languages, words are not separated by spaces.
const SECTION_SUB_WORDS = [
  'クション', // ja-JP
  '部区', // zh-CN
  '部區', // zh-HK
].map((x) => removeAccents(x.toLowerCase()));

/** Convert a section name into its normalized form (lowercase, without "section", without accents) */
export function normalizeSectionName(name: string) {
  name = removeAccents(name.toLowerCase());
  // remove all non letter characters
  name = name.replace(/[^\p{Letter}]+/gu, ' ');
  name = name.trim();
  for (const word of SECTION_WORDS) {
    if (name.startsWith(word)) {
      name = name.substring(word.length);
    }
    if (name.endsWith(word)) {
      name = name.substring(0, name.length - word.length);
    }
  }
  for (const word of SECTION_SUB_WORDS) {
    name = name.replaceAll(word, '');
  }
  return name.trim();
}

function removeAccents(s: string) {
  return s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .normalize('NFC');
}

function cleanClassNamePart(part: string): string {
  return normalizeSectionName(part)
    .replace(/[^a-z0-9\-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

type BlockNodeTag = 'div' | 'footer' | 'header' | 'main' | 'section';

function createBlocksNode(
  id: string,
  classes: string[],
  children: (NestedNode | NestedNode[] | undefined)[],
  tag: BlockNodeTag = 'div',
): NestedTagNode {
  return {
    _id: id,
    type: 'Block',
    tag,
    classes,
    children: children.flat().filter(exists),
    data: { text: false, tag },
  };
}

export function mergeIX2(ix2s: readonly IX2[]): IX2 {
  return {
    events: ix2s.flatMap((x) => x.events),
    interactions: ix2s.flatMap((x) => x.interactions),
    actionLists: ix2s.flatMap((x) => x.actionLists),
  };
}
/**
 * Adds a prefix to all IDs (except style IDs) in a component, so it can safely be merged with other components.
 **/
export function prefixComponentNodeIds(
  prefix: string,
  component: Component,
): Component {
  const allIds = component.payload.nodes.map((x) => x._id);
  const nodeIdUpdates = new Map(allIds.map((x) => [x, `${prefix}${x}`]));
  // IX2 has a lot of "<pageId>|<nodeId>" strings, so we can't require fullString
  component = applyIdUpdatesYolo(component, nodeIdUpdates, {
    fullString: false,
  });

  const ix2IdUpdates = new Map<string, string>();
  for (const e of component.payload.ix2.events) {
    ix2IdUpdates.set(e.id, `${prefix}${e.id}`);
  }
  for (const i of component.payload.ix2.interactions) {
    ix2IdUpdates.set(i.id, `${prefix}${i.id}`);
  }
  for (const a of component.payload.ix2.actionLists) {
    ix2IdUpdates.set(a.id, `${prefix}${a.id}`);
  }
  component = applyIdUpdatesYolo(component, ix2IdUpdates, { fullString: true });

  return component;
}

/**
 * Applies ID updates, without regard to the data structure.
 * We use this for node ids because we don't parse IX2 fully.
 **/
function applyIdUpdatesYolo(
  data: unknown,
  rawIdUpdates: ReadonlyMap<string, string>,
  { fullString }: { fullString: boolean },
) {
  let string = JSON.stringify(data);
  for (const [k, v] of resolveToParents(rawIdUpdates)) {
    const wrap = fullString ? '"' : '';
    string = string.replace(
      new RegExp(`${wrap}${regexEscape(k)}${wrap}`, 'g'),
      `${wrap}${v}${wrap}`,
    );
  }
  return JSON.parse(string);
}

/** Resolve any double updates (i.e. A -> B, B -> C becomes A -> C, B -> C) */
function resolveToParents(
  raw: ReadonlyMap<string, string>,
): ReadonlyMap<string, string> {
  function resolve(id: string): string {
    const result = raw.get(id);
    if (result == null) return id;
    return result;
  }
  return new Map([...raw].map(([k, v]) => [k, resolve(v)]));
}

function regexEscape(s: string) {
  return s.replace(/[/\-\\^$*+?.()|[\]{}]/g, '\\$&');
}

export function mergeAssets(components: readonly WfAsset[][]): WfAsset[] {
  const result = new Map<string, WfAsset>();
  // Take the 1st asset by ID.  I don't know much about the asset system, hopefully this is ok.
  for (const assetList of components) {
    for (const asset of assetList) {
      result.set(asset._id, asset);
    }
  }
  return [...result.values()];
}

function createEmptyClass(name: string): ComponentStyle {
  return {
    _id: `empty-class-${name}`,
    namespace: '',
    fake: false,
    type: 'class',
    name: name,
    sel: `.${name}`,
    comb: '',
    styleLess: '',
    children: [],
    variants: {},
    affects: {},
  };
}
